import React, { useState } from 'react';
import { useRouteMatch } from 'react-router-dom';
import { List, Map } from 'immutable';
import moment from 'moment';

import SettingRow from 'flow/components/Setting/SettingRow';
import Divider from 'flow/components/ui-components/Divider';
import Icon from 'flow/components/ui-components/Icon';
import RouteLink from 'flow/components/ui-components/RouteLink';
import SettingsButton from 'flow/components/ui-components/SettingsButton';
import Toggle from 'flow/components/ui-components/Toggle';
import { useModal } from 'flow/contexts/ModalProvider';
import useAppContext from 'flow/hooks/useAppContext';
import { Calendar, OpeningHour } from 'flow/types/Calendar';
import { Profile } from 'flow/types/Profile';
import { TypedMap } from 'flow/types/TypedMap';
import {
    canUseExternalCalendar,
    hasMatchingWeekdays,
    isAllDay,
} from 'flow/utils/calendar-utils';
import { timeFormater } from 'flow/utils/date-time-utils';

const WeekDay = [
    'monday',
    'tuesday',
    'wednesday',
    'thursday',
    'friday',
    'saturday',
    'sunday',
    'unknown',
];

interface Props {
    calendar: TypedMap<Calendar>;
    profiles: List<TypedMap<Profile>>;
    isExtension: boolean;
    handleUpdateMoToFri: () => void;
    handleUpdateOpeningHours: (updatedOpeningHours: TypedMap<Calendar>) => void;
}

const CalendarSettings = ({
    calendar,
    profiles,
    isExtension,
    handleUpdateMoToFri,
    handleUpdateOpeningHours,
}: Props) => {
    const { i18next } = useAppContext();
    const { openModal } = useModal();
    const match = useRouteMatch();

    const getOpeningHours = () => {
        let openingHours = Map();
        WeekDay.forEach((day) => {
            openingHours = openingHours.set(
                day,
                calendar.getIn(['openingHours', day], Map()),
            );
        });

        return openingHours;
    };

    const openingHours = getOpeningHours();
    const [varies, setVaries] = useState(
        !hasMatchingWeekdays(openingHours, openingHours.first()),
    );
    const [monToFri, setMonToFri] = useState(
        !hasMatchingWeekdays(openingHours, openingHours.first()),
    );

    const getTime = (start: string, end: string) => {
        if (start === end) {
            return isExtension
                ? i18next.t('profile.standard.leftfortheday')
                : i18next.t('pbx.closed');
        }

        const startDate = moment(start).local();
        const endDate = moment(end).local();
        const isWholeDay = isAllDay(startDate, endDate);

        if (isWholeDay) {
            return isExtension
                ? i18next.t('profile.standard.available')
                : i18next.t('pbx.open');
        }

        return timeFormater(start) + ' - ' + timeFormater(end);
    };

    const getLimitedOpeningHours = () =>
        Map()
            .set(
                'monday-friday',
                calendar.getIn(['openingHours', 'monday'], Map()),
            )
            .set(
                'saturday',
                calendar.getIn(['openingHours', 'saturday'], Map()),
            )
            .set('sunday', calendar.getIn(['openingHours', 'sunday'], Map()));

    const handleMoToFri = (value: boolean) => {
        if (varies && !value) {
            openModal('confirmationDialog', {
                title: i18next.t('settings.profile.warning'),
                description: i18next.t(
                    'calendar.OpeningHours.ShowAllWeekdays.Alert.Message',
                ),
                handleAccept: onHandleUpdateMoToFri,
            });
        } else {
            setMonToFri(value);
        }
    };

    const onHandleUpdateMoToFri = () => {
        handleUpdateMoToFri();
        setVaries(false);
        setMonToFri(false);
    };

    if (!calendar) {
        return null;
    }

    return (
        <>
            {calendar.get('editable') ? (
                <SettingRow
                    cta={
                        <Toggle
                            value={monToFri}
                            onChange={handleMoToFri}
                            text={i18next.t(
                                'calendar.OpeningHours.ShowAllWeekdays',
                            )}
                        />
                    }
                >
                    {i18next.t('calendar.OpeningHours.ShowAllWeekdays')}
                </SettingRow>
            ) : null}

            <Divider />

            {(monToFri ? getOpeningHours : getLimitedOpeningHours)()
                .map((openingHours: TypedMap<OpeningHour>, day: string) => {
                    if (!openingHours.size) {
                        return null;
                    }

                    if (!calendar.get('editable')) {
                        return (
                            <div
                                key={day}
                                className="flex w-full items-center justify-between rounded-sm px-2 py-3 text-gray-300"
                            >
                                <div className="flex items-center">
                                    {i18next.t(day)}
                                </div>
                                <div className="flex items-center">
                                    <div className="flex items-center gap-x-2">
                                        <Icon
                                            iconKey="lock"
                                            className="rounded-sm p-0.5"
                                        />
                                    </div>
                                </div>
                            </div>
                        );
                    }

                    return (
                        <SettingsButton
                            key={day}
                            onClick={() =>
                                openModal('setOpeningHour', {
                                    calendar,
                                    profiles,
                                    isExtension,
                                    handleUpdateOpeningHours,
                                    selectedDay: day,
                                })
                            }
                            description={getTime(
                                openingHours.get('start'),
                                openingHours.get('end'),
                            )}
                        >
                            {i18next.t(day)}
                        </SettingsButton>
                    );
                })
                .valueSeq()}

            <Divider />

            <RouteLink to={`${match.url}/events`}>
                {i18next.t('calendar.other.events')}
            </RouteLink>

            {canUseExternalCalendar(
                calendar.getIn(['configurationDTO', 'allowedCalendarSources']),
            ) ? (
                <RouteLink to={`${match.url}/external`}>
                    {i18next.t('calendar.other.external')}
                </RouteLink>
            ) : null}
        </>
    );
};

export default CalendarSettings;
